import { z } from 'zod';
import { COMMENT_BODY_MAX, CommentVisibilitySchema } from './comments';
import { FieldKeySchema } from './fields/registry';
import { FieldRefSchema, FilterNodeSchema, SearchRequestSchema } from './query/ast';

/* ----------------------------- limits ----------------------------- */

export const SUMMARY_MAX = 500;
/** Data API returns at most 64 KB per field (ADR-0015 §3). */
export const DESCRIPTION_MAX = 60_000;
/** Serialized `ticket.fields` document cap; the API answers 413 `ticket.fields_too_large`. */
export const FIELDS_DOCUMENT_MAX_BYTES = 60_000;
export const FIELDS_TOO_LARGE = 'ticket.fields_too_large';
export const LABELS_MAX = 50;
export const LABEL_MAX_LENGTH = 40;
export const BULK_MAX_TARGETS = 1000;
export const EXPORT_COLUMNS_MAX = 50;

/** UTF-8 size of the JSON document, the unit the Data API cap is expressed in. */
export const fieldsDocumentBytes = (fields: Record<string, unknown>): number =>
  new TextEncoder().encode(JSON.stringify(fields)).length;

/* ----------------------------- keys ----------------------------- */

/** `IT-42`: project key (ADR-0013) dash ticket number. */
export const TICKET_KEY_PATTERN = /^([A-Z][A-Z0-9]{1,9})-([1-9][0-9]{0,8})$/;
export const TicketKeySchema = z.string().regex(TICKET_KEY_PATTERN, 'ticket key such as IT-42');
export type TicketKey = z.infer<typeof TicketKeySchema>;

export const ticketKey = (projectKey: string, number: number): string => `${projectKey}-${number}`;

export function parseTicketKey(key: string): { projectKey: string; number: number } | null {
  const m = TICKET_KEY_PATTERN.exec(key);
  if (!m?.[1] || !m[2]) {
    return null;
  }
  return { projectKey: m[1], number: Number(m[2]) };
}

/** Path parameter of `/tickets/{idOrKey}`: a UUID or a (possibly former) key. */
export const TicketIdOrKeySchema = z.union([z.uuid(), TicketKeySchema]);

/* ----------------------------- history event types ----------------------------- */

export const TICKET_EVENT_TYPES = [
  'created',
  'field_changed',
  'transitioned',
  'admin_moved',
  'acknowledged',
  'commented',
  'comment_edited',
  'comment_deleted',
  'attachment_added',
  'watcher_added',
  'watcher_removed',
  'link_added',
  'link_removed',
  'moved',
  'cloned_from',
  'assigned',
] as const;
export const TicketEventTypeSchema = z.enum(TICKET_EVENT_TYPES);
export type TicketEventType = z.infer<typeof TicketEventTypeSchema>;

/* ----------------------------- value pieces ----------------------------- */

export const LabelSchema = z.string().trim().min(1).max(LABEL_MAX_LENGTH);
export const LabelsSchema = z.array(LabelSchema).max(LABELS_MAX);

/** `{ cf_xxxxxxxx: value }`; values are validated by the project's schema builder. */
export const TicketFieldsSchema = z.record(FieldKeySchema, z.unknown());
export type TicketFields = z.infer<typeof TicketFieldsSchema>;

const nullableUuid = z.uuid().nullable();
const DateTimeSchema = z.iso.datetime({ offset: true });

/* ----------------------------- read models ----------------------------- */

export const TicketSchema = z.object({
  id: z.uuid(),
  key: TicketKeySchema,
  number: z.int().min(1),
  projectId: z.uuid(),
  issueTypeId: z.uuid(),
  statusId: z.uuid(),
  priorityId: z.uuid().nullable(),
  summary: z.string(),
  description: z.string().nullable(),
  reporterId: z.uuid(),
  assigneeId: z.uuid().nullable(),
  organizationId: z.uuid().nullable(),
  parentId: z.uuid().nullable(),
  labels: z.array(z.string()),
  /** Custom values by `cf_` key; fields the caller may not read are absent. */
  fields: z.record(z.string(), z.unknown()),
  /** Display labels for id-valued fields (`cf_x` → option label, user name), when requested. */
  display: z.record(z.string(), z.string()).optional(),
  version: z.int().min(1),
  acknowledgedAt: z.iso.datetime().nullable(),
  acknowledgedBy: z.uuid().nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  createdBy: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Ticket = z.infer<typeof TicketSchema>;

/**
 * Search / list projection: everything but `description`; with `select` only the requested
 * keys are present, so every property except `id` and `key` is optional.
 */
export const TicketListItemSchema = TicketSchema.omit({ description: true })
  .partial()
  .required({ id: true, key: true });
export type TicketListItem = z.infer<typeof TicketListItemSchema>;

/* ----------------------------- create / update ----------------------------- */

export const CreateTicketSchema = z.strictObject({
  projectId: z.uuid(),
  issueTypeId: z.uuid(),
  summary: z.string().trim().min(1).max(SUMMARY_MAX),
  description: z.string().max(DESCRIPTION_MAX).nullable().default(null),
  priorityId: nullableUuid.optional(),
  assigneeId: nullableUuid.optional(),
  organizationId: nullableUuid.optional(),
  /** Same project; makes this a child ticket (S-05-10). */
  parentId: nullableUuid.optional(),
  labels: LabelsSchema.default([]),
  fields: TicketFieldsSchema.default({}),
  dueAt: DateTimeSchema.nullable().optional(),
});
export type CreateTicket = z.infer<typeof CreateTicketSchema>;
export type CreateTicketInput = z.input<typeof CreateTicketSchema>;

/** `PATCH /tickets/{id}` with `If-Match`. Status changes only through transitions (S-05-03). */
export const UpdateTicketSchema = z
  .strictObject({
    summary: z.string().trim().min(1).max(SUMMARY_MAX),
    description: z.string().max(DESCRIPTION_MAX).nullable(),
    priorityId: nullableUuid,
    assigneeId: nullableUuid,
    organizationId: nullableUuid,
    labels: LabelsSchema,
    /** Merged into the stored document key by key; `null` clears a value. */
    fields: TicketFieldsSchema,
    dueAt: DateTimeSchema.nullable(),
  })
  .partial();
export type UpdateTicket = z.infer<typeof UpdateTicketSchema>;

/* ----------------------------- transitions ----------------------------- */

/** Comment posted together with a transition or an admin status move. */
export const TransitionCommentSchema = z.strictObject({
  body: z.string().min(1).max(COMMENT_BODY_MAX),
  visibility: CommentVisibilitySchema.default('public'),
});
export type TransitionComment = z.infer<typeof TransitionCommentSchema>;

/** `POST /tickets/{id}/transitions/{tid}` (S-05-05). */
export const ExecuteTransitionSchema = z.strictObject({
  fields: TicketFieldsSchema.default({}),
  comment: TransitionCommentSchema.optional(),
});
export type ExecuteTransition = z.infer<typeof ExecuteTransitionSchema>;

/** `POST /tickets/{id}/status`: synthetic admin move without rules (S-05-06). */
export const AdminStatusSchema = z.strictObject({
  statusId: z.uuid(),
  comment: TransitionCommentSchema.optional(),
});
export type AdminStatus = z.infer<typeof AdminStatusSchema>;

/** Item of `GET /tickets/{id}/transitions` (S-05-04). */
export const AvailableTransitionSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  toStatusId: z.uuid(),
  formId: z.uuid().nullable(),
});
export type AvailableTransition = z.infer<typeof AvailableTransitionSchema>;

export const AvailableTransitionsResponseSchema = z.object({
  items: z.array(AvailableTransitionSchema),
});

/* ----------------------------- move / clone ----------------------------- */

/**
 * `POST /tickets/{id}/move` (S-05-12). Custom fields map by identical `cf_` key (tenant-scoped
 * fields) or through `fieldMapping` (source key → target key); the rest lands in
 * `external_ref.unmapped` and is reported back.
 */
export const MoveTicketSchema = z.strictObject({
  projectId: z.uuid(),
  issueTypeId: z.uuid(),
  /** Defaults to the initial status of the target type's published workflow. */
  statusId: z.uuid().optional(),
  fieldMapping: z.record(FieldKeySchema, FieldKeySchema).default({}),
});
export type MoveTicket = z.infer<typeof MoveTicketSchema>;

export const UnmappedFieldSchema = z.object({
  fieldKey: FieldKeySchema,
  label: z.string(),
  value: z.unknown(),
});
export type UnmappedField = z.infer<typeof UnmappedFieldSchema>;

export const MoveResultSchema = z.object({
  ticket: TicketSchema,
  unmapped: z.array(UnmappedFieldSchema),
});
export type MoveResult = z.infer<typeof MoveResultSchema>;

/** `POST /tickets/{id}/clone`; same project when `projectId` is omitted. */
export const CloneTicketSchema = z.strictObject({
  projectId: z.uuid().optional(),
  includeComments: z.boolean().default(false),
});
export type CloneTicket = z.infer<typeof CloneTicketSchema>;

/* ----------------------------- bulk / export ----------------------------- */

export const BULK_ACTION_TYPES = ['edit', 'transition', 'assign'] as const;
export const BULK_EXACTLY_ONE_TARGET = 'bulk.exactly_one_of_ids_or_filter';
export const BULK_TRANSITION_EXACTLY_ONE = 'bulk.exactly_one_of_transition_id_or_key';

export const BulkEditActionSchema = z
  .strictObject({
    type: z.literal('edit'),
    patch: UpdateTicketSchema,
  })
  .refine((a) => Object.keys(a.patch).length > 0, {
    message: 'patch must change at least one property',
    path: ['patch'],
  });

/** Transitions are matched per ticket by id or, across workflows, by stable key. */
export const BulkTransitionActionSchema = z
  .strictObject({
    type: z.literal('transition'),
    transitionId: z.uuid().optional(),
    transitionKey: z.string().min(1).max(40).optional(),
    fields: TicketFieldsSchema.default({}),
  })
  .superRefine((a, ctx) => {
    const given = [a.transitionId, a.transitionKey].filter((v) => v !== undefined).length;
    if (given !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: `${BULK_TRANSITION_EXACTLY_ONE}: provide exactly one of transitionId or transitionKey`,
      });
    }
  });

export const BulkAssignActionSchema = z.strictObject({
  type: z.literal('assign'),
  assigneeId: z.uuid().nullable(),
});

export const BulkActionSchema = z.discriminatedUnion('type', [
  BulkEditActionSchema,
  BulkTransitionActionSchema,
  BulkAssignActionSchema,
]);
export type BulkAction = z.infer<typeof BulkActionSchema>;

/** `POST /tickets/bulk` (S-05-13): targets by explicit ids or by filter, never both. */
export const BulkRequestSchema = z
  .strictObject({
    ids: z.array(z.uuid()).min(1).max(BULK_MAX_TARGETS).optional(),
    filter: FilterNodeSchema.optional(),
    action: BulkActionSchema,
  })
  .superRefine((value, ctx) => {
    const targets = [value.ids, value.filter].filter((v) => v !== undefined).length;
    if (targets !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: `${BULK_EXACTLY_ONE_TARGET}: provide exactly one of ids or filter`,
      });
    }
  });
export type BulkRequest = z.infer<typeof BulkRequestSchema>;
export type BulkRequestInput = z.input<typeof BulkRequestSchema>;

/** 202 body of bulk and export requests. */
export const JobAcceptedSchema = z.object({ jobId: z.uuid() });
export type JobAccepted = z.infer<typeof JobAcceptedSchema>;

/** `POST /exports` (S-05-13): CSV of the query's rows with display values. */
export const ExportRequestSchema = z.strictObject({
  query: SearchRequestSchema,
  columns: z.array(FieldRefSchema).min(1).max(EXPORT_COLUMNS_MAX),
});
export type ExportRequest = z.infer<typeof ExportRequestSchema>;
export type ExportRequestInput = z.input<typeof ExportRequestSchema>;

/* ----------------------------- Bolt 5 (F1): detail, watchers ----------------------------- */

/** Detail `detail` string of the 400 returned when `PATCH /tickets/{id}` carries a status (S-05-03). */
export const STATUS_VIA_TRANSITION = 'ticket.status_via_transition';

/**
 * `GET /tickets/{idOrKey}` (S-05-02): the ticket plus its display map and the caller's hints.
 * `display` keys are snake_case system names (`status_id`, `assignee_id`, …) or `cf_` keys.
 */
export const TicketDetailSchema = TicketSchema.extend({
  display: z.record(z.string(), z.string()),
  /** The caller watches this ticket. */
  watching: z.boolean(),
  /** `ticket.edit`, or `ticket.edit_own` as the reporter. */
  canEdit: z.boolean(),
  /** `ticket.transition` on the ticket (the list endpoint says which transitions apply). */
  canTransition: z.boolean(),
});
export type TicketDetail = z.infer<typeof TicketDetailSchema>;

/** One watcher of `GET /tickets/{id}/watchers` (S-05-10). */
export const WatcherSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  email: z.string(),
  createdAt: z.iso.datetime(),
});
export type Watcher = z.infer<typeof WatcherSchema>;

export const WatcherListResponseSchema = z.object({ items: z.array(WatcherSchema) });
export type WatcherListResponse = z.infer<typeof WatcherListResponseSchema>;
